import { Capacitor } from '@capacitor/core';
import type { SocialLoginPlugin } from '@capgo/capacitor-social-login';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import {
  authFactorsResponseSchema,
  authSessionSchema,
  type AuthFactorSummary,
  type AuthSession,
} from '@secretario/shared';
import { getDevice, writeSession } from '../session.js';
import { IS_FULL } from '../edition.js';
import { isNativeShell } from '../platform.js';
import { apiFetch, withDevice } from './http.js';

/**
 * Segurança da conta: o que a pessoa liga por cima do SMS (passkey, autenticador,
 * e-mail de recuperação, Google). Tudo opcional; nada aqui é pedido para abrir o app.
 */
export const FACTORS_KEY = ['auth-factors'] as const;

export type FactorsResponse = { factors: AuthFactorSummary[]; google: boolean; mail: boolean };

export async function listFactors(): Promise<FactorsResponse> {
  return authFactorsResponseSchema.parse(await apiFetch<unknown>('/v1/auth/factors'));
}

export const removeFactor = (id: string) =>
  apiFetch<void>(`/v1/auth/factors/${encodeURIComponent(id)}`, { method: 'DELETE' });

/** Passkeys só no navegador: dentro do app a origem é https://localhost e o Android não deixa. */
export function passkeysSupported(): boolean {
  return (
    !isNativeShell() &&
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential === 'function' &&
    window.isSecureContext
  );
}

export async function createPasskey(label: string): Promise<{ id: string; label: string }> {
  const optionsJSON = await apiFetch<Parameters<typeof startRegistration>[0]['optionsJSON']>(
    '/v1/auth/passkeys/register/options',
    { method: 'POST', body: {} },
  );
  const response = await startRegistration({ optionsJSON });
  return apiFetch<{ id: string; label: string }>('/v1/auth/passkeys/register/verify', {
    method: 'POST',
    body: { response, label },
  });
}

export async function loginWithPasskey(): Promise<AuthSession> {
  const { key, options } = await apiFetch<{
    key: string;
    options: Parameters<typeof startAuthentication>[0]['optionsJSON'];
  }>('/v1/auth/passkeys/login/options', { method: 'POST', body: {}, auth: false });
  const response = await startAuthentication({ optionsJSON: options });
  const session = authSessionSchema.parse(
    await withDevice(() =>
      apiFetch<unknown>('/v1/auth/passkeys/login/verify', {
        method: 'POST',
        body: { key, response, device: getDevice() },
        auth: false,
      }),
    ),
  );
  writeSession(session);
  return session;
}

export const totpSetup = () =>
  apiFetch<{ secret: string; uri: string }>('/v1/auth/totp/setup', { method: 'POST', body: {} });
export const totpConfirm = (code: string) =>
  apiFetch<{ backupCodes: string[] }>('/v1/auth/totp/confirm', { method: 'POST', body: { code } });

export const emailRequest = (email: string) =>
  apiFetch<{ hint: string }>('/v1/auth/email/request', { method: 'POST', body: { email } });
export const emailConfirm = (code: string) =>
  apiFetch<void>('/v1/auth/email/confirm', { method: 'POST', body: { code } });

export const recoverRequest = (email: string) =>
  apiFetch<{ ok: boolean }>('/v1/auth/recover/request', {
    method: 'POST',
    body: { email },
    auth: false,
  });
export async function recoverVerify(
  email: string,
  code: string,
  totp?: string,
): Promise<AuthSession> {
  const session = authSessionSchema.parse(
    await withDevice(() =>
      apiFetch<unknown>('/v1/auth/recover/verify', {
        method: 'POST',
        body: { email, code, ...(totp ? { totp } : {}), device: getDevice() },
        auth: false,
      }),
    ),
  );
  writeSession(session);
  return session;
}

export const googleLink = (idToken: string) =>
  apiFetch<{ hint: string | null }>('/v1/auth/google/link', { method: 'POST', body: { idToken } });
export async function googleLogin(idToken: string, totp?: string): Promise<AuthSession> {
  const session = authSessionSchema.parse(
    await withDevice(() =>
      apiFetch<unknown>('/v1/auth/google/login', {
        method: 'POST',
        body: { idToken, device: getDevice(), ...(totp ? { totp } : {}) },
        auth: false,
      }),
    ),
  );
  writeSession(session);
  return session;
}

/**
 * Criar a conta só com o Google, sem número (o SMS não chega a toda operadora). Se essa conta
 * Google já entra em alguma conta, o servidor só abre a sessão dela.
 */
export async function googleRegister(
  idToken: string,
  profile: { username: string; displayName: string },
  totp?: string,
): Promise<AuthSession> {
  const session = authSessionSchema.parse(
    await withDevice(() =>
      apiFetch<unknown>('/v1/auth/google/register', {
        method: 'POST',
        body: { idToken, ...profile, device: getDevice(), ...(totp ? { totp } : {}) },
        auth: false,
      }),
    ),
  );
  writeSession(session);
  return session;
}

/** Nome e e-mail do id_token do Google, só para sugerir o nome no cadastro (quem confere é o servidor). */
export function googleClaims(idToken: string): { name: string; email: string } {
  try {
    const part = idToken.split('.')[1] ?? '';
    const json = decodeURIComponent(
      Array.from(atob(part.replace(/-/g, '+').replace(/_/g, '/')))
        .map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join(''),
    );
    const claims = JSON.parse(json) as { name?: unknown; email?: unknown };
    return {
      name: typeof claims.name === 'string' ? claims.name : '',
      email: typeof claims.email === 'string' ? claims.email : '',
    };
  } catch {
    return { name: '', email: '' };
  }
}

/**
 * Google no app instalado: só no Android por enquanto (o iOS pede cliente próprio e Apple
 * junto), e só nos pacotes registrados no Google (loja e teste); a edição do dono fica de fora.
 */
export function nativeGoogleAvailable(): boolean {
  return !IS_FULL && !!GOOGLE_CLIENT_ID && isNativeShell() && Capacitor.getPlatform() === 'android';
}

let socialReady: Promise<SocialLoginPlugin> | null = null;

/**
 * Abre a escolha de conta do Google do próprio Android e devolve o id_token (para o cliente
 * web, que é o que o servidor confere). Cancelar devolve null.
 */
export async function nativeGoogleIdToken(): Promise<string | null> {
  socialReady ??= import('@capgo/capacitor-social-login').then(async ({ SocialLogin }) => {
    await SocialLogin.initialize({ google: { webClientId: GOOGLE_CLIENT_ID, mode: 'online' } });
    return SocialLogin;
  });
  let social: SocialLoginPlugin;
  try {
    social = await socialReady;
  } catch (error) {
    socialReady = null;
    throw error;
  }
  try {
    // Sem `scopes`: e-mail e perfil já vêm por padrão, e pedir escopo no Android exige trocar a
    // MainActivity (o plugin recusa com "You CANNOT use scopes...").
    const { result } = await social.login({ provider: 'google', options: {} });
    return 'idToken' in result ? result.idToken : null;
  } catch (error) {
    const code = (error as { code?: unknown }).code;
    const message = error instanceof Error ? error.message : String(error);
    if (code === 'USER_CANCELLED' || /cancel/i.test(message)) return null;
    throw error;
  }
}

/** Entrar com o número e o código do autenticador (ou um código reserva), sem SMS. */
export async function totpLogin(phone: string, code: string): Promise<AuthSession> {
  const session = authSessionSchema.parse(
    await withDevice(() =>
      apiFetch<unknown>('/v1/auth/totp/login', {
        method: 'POST',
        body: { phone, code, device: getDevice() },
        auth: false,
      }),
    ),
  );
  writeSession(session);
  return session;
}

/**
 * Id do cliente OAuth do Google para o botão no navegador. É um identificador público
 * (cliente "Secretário web", origem https://app.secretarios.net, projeto secretario-511119);
 * `VITE_GOOGLE_CLIENT_ID` troca por outro ou, vazio de propósito (`VITE_GOOGLE_CLIENT_ID=off`),
 * esconde o botão.
 */
const PRODUCTION_GOOGLE_CLIENT_ID =
  '688348099480-rgi5cr8hqgtk9vdbvnduu6e9qemkgsla.apps.googleusercontent.com';
const configuredGoogleClientId = (
  import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined
)?.trim();
export const GOOGLE_CLIENT_ID =
  configuredGoogleClientId === 'off' ? '' : configuredGoogleClientId || PRODUCTION_GOOGLE_CLIENT_ID;

type GoogleId = {
  accounts: {
    id: {
      initialize(config: {
        client_id: string;
        callback: (r: { credential: string }) => void;
      }): void;
      renderButton(parent: HTMLElement, options: Record<string, unknown>): void;
    };
  };
};

/** Carrega o script do Google uma vez e desenha o botão oficial (só no navegador). */
export async function renderGoogleButton(
  parent: HTMLElement,
  onCredential: (idToken: string) => void,
): Promise<boolean> {
  if (!GOOGLE_CLIENT_ID || isNativeShell()) return false;
  const w = window as unknown as { google?: GoogleId };
  if (!w.google) {
    await new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('google_script'));
      document.head.appendChild(script);
    }).catch(() => undefined);
  }
  if (!w.google) return false;
  w.google.accounts.id.initialize({
    client_id: GOOGLE_CLIENT_ID,
    callback: (response) => onCredential(response.credential),
  });
  w.google.accounts.id.renderButton(parent, {
    theme: 'outline',
    size: 'large',
    shape: 'pill',
    text: 'continue_with',
    locale: 'pt-BR',
    width: 300,
  });
  return true;
}
